import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

export default function EmploymentIncomeSection({ data, onChange, isOpen, onOpen }) {
  const [collapsed, setCollapsed] = useState(false);
  const isMarried = data.maritalStatus === 'married';
  const buildName = (prefix, field) =>
    prefix
      ? prefix + field.charAt(0).toUpperCase() + field.slice(1)
      : field;
  const payTypeFields = (prefix = '') => {
    const payType = data[`${prefix}payType`] || data[`${prefix}PayType`];
    if (payType === 'hourly') {
      return (
        <>
          <input
            name={buildName(prefix, 'hourlyRate')}
            value={data[buildName(prefix, 'hourlyRate')] || ''}
            onChange={onChange}
            placeholder="Hourly Rate"
            className="input"
          />
          <input
            name={buildName(prefix, 'hoursWorked')}
            value={data[buildName(prefix, 'hoursWorked')] || ''}
            onChange={onChange}
            placeholder="Hours Worked per Week"
            className="input"
          />
        </>
      );
    } else if (payType === 'salary') {
      return (
        <input
          name={buildName(prefix, 'yearlySalary')}
          value={data[buildName(prefix, 'yearlySalary')] || ''}
          onChange={onChange}
          placeholder="Yearly Salary"
          className="input"
        />
      );
    } else if (payType === 'self-employed') {
      return (
        <input
          name={buildName(prefix, 'netProfit')}
          value={data[buildName(prefix, 'netProfit')] || ''}
          onChange={onChange}
          placeholder="Monthly Net Profit"
          className="input"
        />
      );
    }
    return null;
  };

  const renderEmploymentBlock = (prefix = '') => (
    <>
      <input name={buildName(prefix, 'workPlace')} value={data[buildName(prefix, 'workPlace')] || ''} onChange={onChange} placeholder="Workplace" className="input" />
      <input name={buildName(prefix, 'jobTitle')} value={data[buildName(prefix, 'jobTitle')] || ''} onChange={onChange} placeholder="Job Title" className="input" />
      <select name={buildName(prefix, 'jobDuration')} value={data[buildName(prefix, 'jobDuration')] || ''} onChange={onChange} className="input">
        <option value="">Job Duration (in years)</option>
        <option value="<1">&lt; 1</option>
        {[...Array(21)].map((_, i) => i > 0 && <option key={i} value={i}>{i}</option>)}
      </select>
      {data[buildName(prefix, 'jobDuration')] === '<1' && (
        <>
          <label className="text-sm">Start Date</label>
          <input type="date" name={buildName(prefix, 'startDate')} value={data[buildName(prefix, 'startDate')] || ''} onChange={onChange} className="input" />
        </>
      )}
      <select name={buildName(prefix, 'payType')} value={data[buildName(prefix, 'payType')] || ''} onChange={onChange} className="input">
        <option value="">Pay Type</option>
        <option value="hourly">Hourly</option>
        <option value="salary">Salary</option>
        <option value="self-employed">Self-Employed</option>
      </select>
      <label className="text-sm">Does this job provide pay stubs/payment advices?</label>
      <select
        name={buildName(prefix, 'hasPaymentAdvices')}
        value={data[buildName(prefix, 'hasPaymentAdvices')] || 'yes'}
        onChange={onChange}
        className="input"
      >
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
      {payTypeFields(prefix)}
    </>
  );

  const renderPriorEmployment = (prefix = '') => (
    <>
      <input name={buildName(prefix, 'priorWorkPlace')} value={data[buildName(prefix, 'priorWorkPlace')] || ''} onChange={onChange} placeholder="Prior Workplace" className="input" />
      <input name={buildName(prefix, 'priorJobTitle')} value={data[buildName(prefix, 'priorJobTitle')] || ''} onChange={onChange} placeholder="Prior Job Title" className="input" />
      <select name={buildName(prefix, 'priorJobDuration')} value={data[buildName(prefix, 'priorJobDuration')] || ''} onChange={onChange} className="input">
        <option value="">Job Duration (in years)</option>
        <option value="<1">&lt; 1</option>
        {[...Array(21)].map((_, i) => i > 0 && <option key={i} value={i}>{i}</option>)}
      </select>
      {data[buildName(prefix, 'priorJobDuration')] === '<1' && (
        <>
          <label className="text-sm">Start Date</label>
          <input type="date" name={buildName(prefix, 'priorStartDate')} value={data[buildName(prefix, 'priorStartDate')] || ''} onChange={onChange} className="input" />
        </>
      )}
      <label className="text-sm">End Date</label>
      <input type="date" name={buildName(prefix, 'priorLeavingDate')} value={data[buildName(prefix, 'priorLeavingDate')] || ''} onChange={onChange} className="input" />
      <select name={buildName(prefix, 'priorPayType')} value={data[buildName(prefix, 'priorPayType')] || ''} onChange={onChange} className="input">
        <option value="">Pay Type</option>
        <option value="hourly">Hourly</option>
        <option value="salary">Salary</option>
        <option value="self-employed">Self-Employed</option>
      </select>
      <label className="text-sm">Did this job provide pay stubs/payment advices?</label>
      <select
        name={buildName(prefix ? `${prefix}Prior` : 'prior', 'hasPaymentAdvices')}
        value={data[buildName(prefix ? `${prefix}Prior` : 'prior', 'hasPaymentAdvices')] || 'yes'}
        onChange={onChange}
        className="input"
      >
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
      {payTypeFields(prefix ? `${prefix}Prior` : 'prior')}
    </>
  );

  const renderFixedIncome = (prefix = '') => {
    const sourceField = buildName(prefix, 'fixedIncomeSources');
    const detailsField = buildName(prefix, 'fixedIncomeDetails');
    const sources = ["Social Security", "Veterans Benefits", "Private Pension", "Alimony", "Child Support", "Other"];

    return (
      <div className="bg-white p-4 shadow-xl rounded border">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Fixed Income Sources</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {sources.map(source => (
            <div key={source} className="flex items-center space-x-2">
              <input
                type="checkbox"
                name={sourceField}
                value={source}
                checked={data[sourceField]?.includes(source)}
                onChange={(e) => {
                  const updated = e.target.checked
                    ? [...(data[sourceField] || []), source]
                    : data[sourceField]?.filter(item => item !== source);
                  const updatedDetails = { ...(data[detailsField] || {}) };
                  if (!e.target.checked) delete updatedDetails[source];
                  onChange({ target: { name: sourceField, value: updated } });
                  onChange({ target: { name: detailsField, value: updatedDetails } });
                }}
              />
              <label className="text-sm">{source}</label>
              {data[sourceField]?.includes(source) && (
                source === "Other" ? (
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      placeholder="Description"
                      value={data[detailsField]?.Other?.description || ""}
                      onChange={e => onChange({ target: { name: detailsField, value: { ...data[detailsField], Other: { ...data[detailsField]?.Other, description: e.target.value } } } })}
                      className="input w-40"
                    />
                    <input
                      type="text"
                      placeholder="Amount"
                      value={data[detailsField]?.Other?.amount || ""}
                      onChange={e => onChange({ target: { name: detailsField, value: { ...data[detailsField], Other: { ...data[detailsField]?.Other, amount: e.target.value } } } })}
                      className="input w-24"
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder="Amount"
                    value={data[detailsField]?.[source] || ""}
                    onChange={e => onChange({ target: { name: detailsField, value: { ...data[detailsField], [source]: e.target.value } } })}
                    className="input w-24"
                  />
                )
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between bg-blue-100 px-4 py-2 rounded shadow-xl cursor-pointer" onClick={onOpen}>
        <h2 className="text-lg font-semibold text-gray-800">Employment & Income</h2>
        {isOpen ? <ChevronRightIcon className="w-5 h-5 text-gray-600" /> : <ChevronDownIcon className="w-5 h-5 text-gray-600" />}
      </div>

      {isOpen && (
        <div className="p-4 space-y-6">
          {/* Respondent Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded border p-4 shadow-xl">
              <label className="text-sm font-medium">Are you employed?</label>
              <select name="employmentStatus" value={data.employmentStatus} onChange={onChange} className="input mb-2">
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              {data.employmentStatus === "yes" && renderEmploymentBlock()}
            </div>

            <div className="bg-white rounded border p-4 shadow-xl">
              <label className="text-sm font-medium">Do you have a second job?</label>
              <select name="secondJob" value={data.secondJob} onChange={onChange} className="input mb-2">
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              {data.secondJob === "yes" && renderEmploymentBlock("second")}
            </div>

            <div className="bg-white rounded border p-4 shadow-xl">
              <label className="text-sm font-medium">Prior Employment in last 6 months?</label>
              <select name="priorEmployment" value={data.priorEmployment} onChange={onChange} className="input mb-2">
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              {data.priorEmployment === "yes" && renderPriorEmployment()}
            </div>

            <div>{renderFixedIncome()}</div>
          </div>

          {/* Spouse Section */}
          {isMarried && (
            <>
              <h3 className="text-md font-semibold mt-6">Spouse Employment & Income</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded border p-4 shadow-xl">
                  <label className="text-sm font-medium">Is spouse employed?</label>
                  <select name="spouseEmploymentStatus" value={data.spouseEmploymentStatus} onChange={onChange} className="input mb-2">
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  {data.spouseEmploymentStatus === "yes" && renderEmploymentBlock("spouse")}
                </div>

                <div className="bg-white rounded border p-4 shadow-xl">
                  <label className="text-sm font-medium">Does spouse have a second job?</label>
                  <select name="spouseSecondJob" value={data.spouseSecondJob} onChange={onChange} className="input mb-2">
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  {data.spouseSecondJob === "yes" && renderEmploymentBlock("spouseSecond")}
                </div>

                <div className="bg-white rounded border p-4 shadow-xl">
                  <label className="text-sm font-medium">Spouse Prior Employment?</label>
                  <select name="spousePriorEmployment" value={data.spousePriorEmployment} onChange={onChange} className="input mb-2">
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  {data.spousePriorEmployment === "yes" && renderPriorEmployment("spouse")}
                </div>

                <div>{renderFixedIncome("spouse")}</div>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
